import { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, Calendar, TrendingUp, DollarSign } from 'lucide-react';
import LineChart from '../components/LineChart';
import DataTable from '../components/DataTable';
import DonutChart from '../components/DonutChart';
import { logger } from '../utils/logger';
import { formatNumber } from '../utils/csvParser';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import { STATUS_COLORS, CHART_PALETTE } from '../config/colors';

interface MaintenanceRecord {
  maintenance_id: string;
  truck_id: string;
  maintenance_date: string;
  maintenance_type: string;
  odometer: number;
  labor_hours: number;
  labor_cost: number;
  parts_cost: number;
  total_cost: number;
  facility_location: string;
  downtime: number;
  service_description: string;
}

export default function Maintenance() {
  // Always use logistics backend for maintenance data (ES backend doesn't have maintenance data yet)
  const maintenanceServiceType = 'logistics';
  
  const [maintenanceData, setMaintenanceData] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMaintenanceType, setSelectedMaintenanceType] = useState<string | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<string | null>(null);
  const [selectedTruck, setSelectedTruck] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [trendView, setTrendView] = useState<'cost' | 'downtime'>('cost');

  // Parse CSV data
  const parseCSV = (text: string): MaintenanceRecord[] => {
    const lines = text.trim().split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const records: MaintenanceRecord[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      const record: Record<string, unknown> = {};
      
      headers.forEach((header, index) => {
        const value = values[index] || '';
        const headerLower = header.toLowerCase();
        // Convert numeric fields (check for both 'downtime' and 'downtime_hours', 'odometer_reading' and 'odometer')
        if (['odometer_reading', 'odometer', 'labor_hours', 'labor_cost', 'parts_cost', 'total_cost', 'downtime_hours', 'downtime'].includes(headerLower)) {
          record[headerLower] = parseFloat(value) || 0;
        } else {
          record[headerLower] = value;
        }
      });
      
      // Normalize column names - map downtime_hours to downtime for consistency
      if (record.downtime_hours !== undefined) {
        record.downtime = record.downtime_hours;
      }
      if (record.odometer_reading !== undefined) {
        record.odometer = record.odometer_reading;
      }
      
      // Ensure all required fields exist
      if (record.maintenance_id && record.truck_id) {
        records.push(record as unknown as MaintenanceRecord);
      }
    }
    
    return records;
  };

  // Load maintenance data (independent of period)
  useEffect(() => {
    setLoading(true);
    let isCancelled = false;

    import('../config/api').then(({ API_ENDPOINTS }) => {
      // Always use logistics backend for maintenance data (ES backend doesn't have maintenance data yet)
      const maintenanceEndpoint = API_ENDPOINTS.maintenanceRecords(maintenanceServiceType);
    
      authenticatedFetch(maintenanceEndpoint)
        .then(res => {
          if (!res.ok) {
            // If endpoint doesn't exist yet or file not found, return empty data
            if (res.status === 404) {
              logger.warn('Maintenance endpoint not found, using empty data');
              return '';
            }
            // For other errors, try to get text response, otherwise return empty
            return res.text().catch(() => '');
          }
          return res.text();
        })
        .then((text: string) => {
          if (isCancelled) return;
          
          if (text && text.trim() !== '') {
            const parsed = parseCSV(text);
            setMaintenanceData(parsed);
          } else {
            // Empty response or just headers - set empty data
            setMaintenanceData([]);
          }
          setLoading(false);
        })
        .catch(err => {
          if (isCancelled) return;
          logger.error('Error loading maintenance data:', err);
          // On any error, set empty data and continue
          setMaintenanceData([]);
          setLoading(false);
        });
    });

    return () => {
      isCancelled = true;
    };
  }, [maintenanceServiceType]);

  // Filter data based on selections
  const filteredData = useMemo(() => {
    let filtered = [...maintenanceData];
    
    if (selectedMaintenanceType) {
      filtered = filtered.filter(r => r.maintenance_type === selectedMaintenanceType);
    }
    
    if (selectedFacility) {
      filtered = filtered.filter(r => r.facility_location === selectedFacility);
    }
    
    if (selectedTruck) {
      filtered = filtered.filter(r => r.truck_id === selectedTruck);
    }
    
    return filtered;
  }, [maintenanceData, selectedMaintenanceType, selectedFacility, selectedTruck]);

  // Format number with K notation
  const formatWithK = (value: number, decimals: number = 1): string => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(decimals)}K`;
    }
    return value.toFixed(decimals);
  };

  // Calculate KPIs
  const kpis = useMemo(() => {
    const totalCost = filteredData.reduce((sum, r) => sum + r.total_cost, 0);
    const totalDowntime = filteredData.reduce((sum, r) => sum + r.downtime, 0);
    const maintenanceEvents = filteredData.length;
    const uniqueVehicles = new Set(filteredData.map(r => r.truck_id)).size;
    const avgCostPerVehicle = uniqueVehicles > 0 ? totalCost / uniqueVehicles : 0;
    
    // Calculate total odometer delta (approximate - would need previous odometer readings)
    const odometerDelta = filteredData.length > 0 
      ? Math.max(...filteredData.map(r => r.odometer)) - Math.min(...filteredData.map(r => r.odometer))
      : 0;
    const avgCostPerMile = odometerDelta > 0 ? totalCost / odometerDelta : 0;
    
    // Calculate vehicles due for service (vehicles with no recent maintenance)
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const recentMaintenance = filteredData.filter(r => {
      const maintDate = new Date(r.maintenance_date);
      return maintDate >= thirtyDaysAgo;
    });
    const vehiclesWithRecentMaintenance = new Set(recentMaintenance.map(r => r.truck_id));
    const allVehicles = new Set(filteredData.map(r => r.truck_id));
    const vehiclesDueForService = allVehicles.size - vehiclesWithRecentMaintenance.size;
    
    return {
      totalCost,
      totalDowntime,
      maintenanceEvents,
      avgCostPerVehicle,
      avgCostPerMile,
      vehiclesDueForService,
      uniqueVehicles
    };
  }, [filteredData]);

  // Prepare trend data
  const trendData = useMemo(() => {
    if (filteredData.length === 0) return { cost: [], downtime: [], frequency: [] };
    
    // Group by date based on dateRange
    const grouped: Record<string, { cost: number; downtime: number; count: number; labor: number; parts: number }> = {};
    
    filteredData.forEach(record => {
      const date = new Date(record.maintenance_date);
      let key: string;
      
      if (dateRange === 'daily') {
        key = date.toISOString().split('T')[0];
      } else if (dateRange === 'weekly') {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        key = weekStart.toISOString().split('T')[0];
      } else {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
      
      if (!grouped[key]) {
        grouped[key] = { cost: 0, downtime: 0, count: 0, labor: 0, parts: 0 };
      }
      
      grouped[key].cost += record.total_cost;
      grouped[key].downtime += record.downtime;
      grouped[key].count += 1;
      grouped[key].labor += record.labor_cost;
      grouped[key].parts += record.parts_cost;
    });
    
    const sortedKeys = Object.keys(grouped).sort();
    
    return {
      cost: sortedKeys.map(key => ({
        label: key,
        value: grouped[key].cost,
        date: key
      })),
      downtime: sortedKeys.map(key => ({
        label: key,
        value: grouped[key].downtime,
        date: key
      })),
      frequency: sortedKeys.map(key => ({
        label: key,
        value: grouped[key].count,
        date: key
      })),
      costBreakdown: sortedKeys.map(key => ({
        label: key,
        labor: grouped[key].labor,
        parts: grouped[key].parts,
        date: key
      }))
    };
  }, [filteredData, dateRange]);

  // Prepare cost breakdown by type
  const costByType = useMemo(() => {
    const breakdown: Record<string, { cost: number; count: number; downtime: number }> = {};
    
    filteredData.forEach(record => {
      if (!breakdown[record.maintenance_type]) {
        breakdown[record.maintenance_type] = { cost: 0, count: 0, downtime: 0 };
      }
      breakdown[record.maintenance_type].cost += record.total_cost;
      breakdown[record.maintenance_type].count += 1;
      breakdown[record.maintenance_type].downtime += record.downtime;
    });
    
    return Object.entries(breakdown).map(([type, data]) => ({
      label: type,
      value: data.cost,
      count: data.count,
      downtime: data.downtime
    })).sort((a, b) => b.value - a.value);
  }, [filteredData]);

  // Get unique values for filters
  const uniqueMaintenanceTypes = useMemo(() => {
    return Array.from(new Set(maintenanceData.map(r => r.maintenance_type))).sort();
  }, [maintenanceData]);

  const uniqueFacilities = useMemo(() => {
    return Array.from(new Set(maintenanceData.map(r => r.facility_location))).sort();
  }, [maintenanceData]);

  const uniqueTrucks = useMemo(() => {
    return Array.from(new Set(maintenanceData.map(r => r.truck_id))).sort();
  }, [maintenanceData]);


  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warning mb-4"></div>
          <div className="text-muted-foreground">Loading maintenance data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Maintenance Management</h2>
          <p className="text-muted-foreground text-sm">
            Track vehicle maintenance schedules, costs, and optimise maintenance operations.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg shadow-sm p-4 border border-border mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">Maintenance Type</label>
            <select
              value={selectedMaintenanceType || ''}
              onChange={(e) => setSelectedMaintenanceType(e.target.value || null)}
              className="w-full bg-secondary text-white rounded px-3 py-2 border border-accent focus:border-success focus:outline-none"
            >
              <option value="">All Types</option>
              {uniqueMaintenanceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">Facility Location</label>
            <select
              value={selectedFacility || ''}
              onChange={(e) => setSelectedFacility(e.target.value || null)}
              className="w-full bg-secondary text-white rounded px-3 py-2 border border-accent focus:border-success focus:outline-none"
            >
              <option value="">All Facilities</option>
              {uniqueFacilities.map(facility => (
                <option key={facility} value={facility}>{facility}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">Vehicle ID</label>
            <select
              value={selectedTruck || ''}
              onChange={(e) => setSelectedTruck(e.target.value || null)}
              className="w-full bg-secondary text-white rounded px-3 py-2 border border-accent focus:border-success focus:outline-none"
            >
              <option value="">All Vehicles</option>
              {uniqueTrucks.map(truck => (
                <option key={truck} value={truck}>{truck}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">Time Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as 'daily' | 'weekly' | 'monthly')}
              className="w-full bg-secondary text-white rounded px-3 py-2 border border-accent focus:border-success focus:outline-none"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPI Cards - 4 cards in a row, styled like ProfitabilityReport */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Maintenance Cost */}
          <div 
            className="relative overflow-hidden bg-gradient-to-br from-chart-3/20 via-card to-background rounded-xl border border-chart-3/30 p-6 shadow-xl hover:shadow-chart-3/20 transition-all duration-500 transform hover:scale-105 cursor-pointer"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-chart-3/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-chart-3 rounded-full animate-pulse"></div>
                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Maintenance Cost</div>
              </div>
              <div className="text-4xl font-bold text-white mb-1">
                £{formatWithK(kpis.totalCost, 1)}
              </div>
              <div className="text-xs text-muted-foreground font-medium">{kpis.maintenanceEvents} events</div>
            </div>
          </div>

          {/* Avg Cost per Vehicle */}
          <div 
            className="relative overflow-hidden bg-gradient-to-br from-chart-1/20 via-card to-background rounded-xl border border-chart-1/30 p-6 shadow-xl hover:shadow-chart-1/20 transition-all duration-500 transform hover:scale-105 cursor-pointer"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-chart-1/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-chart-1 rounded-full animate-pulse"></div>
                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Avg Cost per Vehicle</div>
              </div>
              <div className="text-4xl font-bold text-white mb-1">
                £{formatWithK(kpis.avgCostPerVehicle, 1)}
              </div>
              <div className="text-xs text-muted-foreground font-medium">{kpis.uniqueVehicles} vehicles serviced</div>
            </div>
          </div>

          {/* Total Downtime */}
          <div 
            className="relative overflow-hidden bg-gradient-to-br from-chart-2/20 via-card to-background rounded-xl border border-chart-2/30 p-6 shadow-xl hover:shadow-chart-2/20 transition-all duration-500 transform hover:scale-105 cursor-pointer"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-chart-2/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-chart-2 rounded-full animate-pulse"></div>
                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Downtime</div>
              </div>
              <div className="text-4xl font-bold text-white mb-1">
                {formatWithK(kpis.totalDowntime, 1)} hrs
              </div>
              <div className="text-xs text-muted-foreground font-medium">Vehicle unavailability</div>
            </div>
          </div>

          {/* Maintenance Events */}
          <div 
            className="relative overflow-hidden bg-gradient-to-br from-chart-5/20 via-card to-background rounded-xl border border-chart-5/30 p-6 shadow-xl hover:shadow-chart-5/20 transition-all duration-500 transform hover:scale-105 cursor-pointer"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-chart-5/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-chart-5 rounded-full animate-pulse"></div>
                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Maintenance Events</div>
              </div>
              <div className="text-4xl font-bold text-white mb-1">
                {kpis.maintenanceEvents}
              </div>
              <div className="text-xs text-muted-foreground font-medium">Total service events</div>
            </div>
          </div>
        </div>

        {/* Cost Distribution, Maintenance Types, and Maintenance Trends */}
        <div className="space-y-4">
          {/* Cost Distribution and Maintenance Types - Side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Cost Distribution Pie Chart */}
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border hover:shadow-lg hover:shadow-chart-3/50 hover:border-chart-3 transition-all flex flex-col">
              <h3 className="text-lg font-semibold text-white pb-3">Cost Distribution</h3>
              {costByType.length > 0 ? (
                <div className="[&>div]:bg-transparent [&>div]:border-0 [&>div]:shadow-none [&>div]:p-0">
                  <DonutChart
                    data={costByType.map((item, index) => {
                      const colors = [CHART_PALETTE[2], CHART_PALETTE[0], CHART_PALETTE[1], CHART_PALETTE[3], CHART_PALETTE[4], CHART_PALETTE[6], CHART_PALETTE[9]];
                      return { 
                        label: item.label, 
                        value: item.value,
                        color: colors[index % colors.length]
                      };
                    })}
                    title=""
                    legendOffset="-mt-16"
                  />
                </div>
              ) : (
                <div className="text-muted-foreground text-center py-8">No data available</div>
              )}
            </div>

            {/* Maintenance Types Distribution Chart */}
            <div className="bg-card rounded-lg shadow-sm p-6 border border-border hover:shadow-lg hover:shadow-chart-1/50 hover:border-chart-1 transition-all flex flex-col">
              <h3 className="text-lg font-semibold text-white pb-3">Maintenance Types</h3>
              {costByType.length > 0 ? (
                <div className="[&>div]:bg-transparent [&>div]:border-0 [&>div]:shadow-none [&>div]:p-0">
                  <DonutChart
                    data={costByType.map((item, index) => {
                      const colors = [CHART_PALETTE[2], CHART_PALETTE[0], CHART_PALETTE[1], CHART_PALETTE[3], CHART_PALETTE[4], CHART_PALETTE[6], CHART_PALETTE[9]];
                      return { 
                        label: item.label, 
                        value: item.count,
                        color: colors[index % colors.length]
                      };
                    })}
                    title=""
                    legendOffset="-mt-16"
                  />
                </div>
              ) : (
                <div className="text-muted-foreground text-center py-8">No data available</div>
              )}
            </div>
          </div>

          {/* Maintenance Trends */}
          <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Maintenance Trends</h3>
              <select
                value={trendView}
                onChange={(e) => setTrendView(e.target.value as 'cost' | 'downtime')}
                className="px-3 py-1.5 text-sm font-medium rounded-md transition-all border bg-secondary text-muted-foreground border-accent hover:bg-accent hover:border-success hover:text-warning hover:shadow-lg hover:shadow-warning/50 focus:outline-none focus:ring-2 focus:ring-warning focus:border-warning cursor-pointer"
                style={{
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="cost" className="bg-card text-muted-foreground">Total Cost</option>
                <option value="downtime" className="bg-card text-muted-foreground">Total Downtime</option>
              </select>
            </div>
            {trendView === 'cost' ? (
              trendData.cost.length > 0 ? (
                <LineChart
                  data={trendData.cost}
                  title="Total Cost"
                  yAxisLabel="Cost (£)"
                  color={STATUS_COLORS.success}
                  formatYAxisAsK={true}
                />
              ) : (
                <div className="text-muted-foreground text-center py-8">No data available</div>
              )
            ) : (
              trendData.downtime.length > 0 ? (
                <LineChart
                  data={trendData.downtime}
                  title="Total Downtime"
                  yAxisLabel="Hours"
                  color={STATUS_COLORS.destructive}
                  formatYAxisAsK={true}
                />
              ) : (
                <div className="text-muted-foreground text-center py-8">No data available</div>
              )
            )}
          </div>
        </div>
      </div>

      {/* Cost Breakdown by Type */}
      {costByType.length > 0 ? (
        <DataTable
          columns={[
            { key: 'Type', label: 'Maintenance Type' },
            { key: 'Count', label: 'Events', align: 'right' },
            { key: 'Total Cost', label: 'Total Cost', align: 'right' },
            { key: 'Avg Cost', label: 'Avg Cost', align: 'right' },
            { key: 'Avg Downtime', label: 'Avg Downtime', align: 'right' }
          ]}
          data={costByType.map(item => ({
            Type: item.label,
            Count: item.count,
            'Total Cost': `£${formatWithK(item.value, 1)}`,
            'Avg Cost': `£${formatWithK(item.value / item.count, 1)}`,
            'Avg Downtime': `${formatNumber(item.downtime / item.count, 2)} hrs`
          }))}
          title="Cost Breakdown"
          hideExport
        />
      ) : (
        <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
          <div className="text-muted-foreground text-center py-8">No data available</div>
        </div>
      )}


      {/* Placeholder for Future Features */}
      <div className="bg-card rounded-lg shadow-sm p-6 border border-border">
        <h3 className="text-lg font-semibold text-white mb-4">Advanced Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-muted-foreground">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-success mt-0.5" />
            <div>
              <p className="font-medium text-white">Predictive Maintenance</p>
              <p className="text-sm">Predict future maintenance needs based on odometer readings and historical patterns</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <TrendingUp className="w-5 h-5 text-success mt-0.5" />
            <div>
              <p className="font-medium text-white">Schedule Optimisation</p>
              <p className="text-sm">Optimise maintenance schedules to minimise costs and downtime</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-success mt-0.5" />
            <div>
              <p className="font-medium text-white">Anomaly Detection</p>
              <p className="text-sm">Identify unusual maintenance patterns and cost anomalies</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-success mt-0.5" />
            <div>
              <p className="font-medium text-white">ROI Analysis</p>
              <p className="text-sm">Calculate return on investment for preventive maintenance programmes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
